import React, { useState, useEffect, useCallback } from 'react';

// ============================================================================
// SHIKAKU - Daily Rectangle Division Puzzle
// ============================================================================
// A standalone React component for embedding on your website.
// 
// Usage: import Shikaku from './Shikaku';
//        <Shikaku />
//
// Features:
// - One puzzle per day, resets at midnight UTC
// - Same puzzle for all players globally
// - Procedurally generated (guaranteed solvable)
// - Progress saved to localStorage
// - Shareable results
// - Touch/mobile support
// ============================================================================

// Seeded random number generator (Mulberry32)
const mulberry32 = (seed) => {
  return () => {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
};

// Game configuration
const DIFFICULTIES = [
  { name: 'Easy', size: 5 },
  { name: 'Medium', size: 6 },
  { name: 'Hard', size: 7 },
];

const LAUNCH_DATE = new Date('2025-01-27T00:00:00Z'); // Set your launch date
const STORAGE_KEY_PREFIX = 'shikaku';

// Colours for placed rectangles (soft, harmonious palette)
const RECT_COLORS = [
  '#E8D4B8', '#B8D4C8', '#C8D4E8', '#E8C8D4', '#D4E8D4', '#E8E0C8',
  '#D4C8E8', '#C8E8E0', '#E8D8C8', '#D0D8E8', '#E0E8D0', '#E8D0D8',
];

// Generate a valid Shikaku puzzle by working backwards from a solution
const generatePuzzle = (size, seed) => {
  const random = mulberry32(seed);
  const solution = [];
  const covered = Array(size).fill(null).map(() => Array(size).fill(false));
  
  const hasUncovered = () => covered.some(row => row.some(cell => !cell));
  
  const findFirstUncovered = () => {
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (!covered[r][c]) return { row: r, col: c };
      }
    }
    return null;
  };
  
  const getPossibleRectangles = (start) => {
    const rects = [];
    const maxWidth = Math.min(6, size - start.col);
    const maxHeight = Math.min(6, size - start.row);
    
    for (let h = 1; h <= maxHeight; h++) {
      for (let w = 1; w <= maxWidth; w++) {
        let valid = true;
        for (let r = start.row; r < start.row + h && valid; r++) {
          for (let c = start.col; c < start.col + w && valid; c++) {
            if (covered[r][c]) valid = false;
          }
        }
        if (valid) {
          const area = h * w;
          if (area >= 2 && area <= 9) {
            rects.push({ minRow: start.row, maxRow: start.row + h - 1, minCol: start.col, maxCol: start.col + w - 1, area });
          }
        }
      }
    }
    return rects.sort(() => random() - 0.5);
  };
  
  while (hasUncovered()) {
    const start = findFirstUncovered();
    if (!start) break;
    
    const possibleRects = getPossibleRectangles(start);
    if (possibleRects.length === 0) {
      solution.push({ minRow: start.row, maxRow: start.row, minCol: start.col, maxCol: start.col, area: 1 });
      covered[start.row][start.col] = true;
    } else {
      const rect = possibleRects[Math.floor(random() * possibleRects.length)];
      solution.push(rect);
      for (let r = rect.minRow; r <= rect.maxRow; r++) {
        for (let c = rect.minCol; c <= rect.maxCol; c++) {
          covered[r][c] = true;
        }
      }
    }
  }
  
  const grid = Array(size).fill(null).map(() => Array(size).fill(0));
  for (const rect of solution) {
    const cells = [];
    for (let r = rect.minRow; r <= rect.maxRow; r++) {
      for (let c = rect.minCol; c <= rect.maxCol; c++) {
        cells.push({ r, c });
      }
    }
    const pos = cells[Math.floor(random() * cells.length)];
    grid[pos.r][pos.c] = rect.area;
  }
  
  return { grid, size };
};

// Date utilities
const getDayNumber = () => {
  const now = new Date();
  const utcNow = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const utcLaunch = Date.UTC(LAUNCH_DATE.getUTCFullYear(), LAUNCH_DATE.getUTCMonth(), LAUNCH_DATE.getUTCDate());
  return Math.floor((utcNow - utcLaunch) / (1000 * 60 * 60 * 24));
};

const getTodaysDifficulty = (dayNumber) => {
  const random = mulberry32(dayNumber * 7919);
  const roll = random();
  if (roll < 0.4) return 0;
  if (roll < 0.8) return 1;
  return 2;
};

const getFormattedDate = () => {
  const now = new Date();
  return now.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const getTimeUntilNextPuzzle = () => {
  const now = new Date();
  const tomorrow = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
  const diff = tomorrow - now;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return `${hours}h ${minutes}m ${seconds}s`;
};

// Storage utilities
const getTodayKey = () => {
  const now = new Date();
  return `${STORAGE_KEY_PREFIX}-${now.getUTCFullYear()}-${now.getUTCMonth() + 1}-${now.getUTCDate()}`;
};

const saveProgress = (data) => {
  try {
    localStorage.setItem(getTodayKey(), JSON.stringify({ ...data, timestamp: Date.now() }));
  } catch {}
};

const getStoredProgress = () => {
  try {
    const data = localStorage.getItem(getTodayKey());
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

// Clipboard utility
const copyToClipboard = async (text, onSuccess, onError) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      onSuccess();
      return;
    }
  } catch {}
  
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    onSuccess();
  } catch {
    onError();
  }
};

// ============================================================================
// COMPONENT
// ============================================================================

const Shikaku = () => {
  const [puzzleData, setPuzzleData] = useState(null);
  const [rectangles, setRectangles] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startCell, setStartCell] = useState(null);
  const [currentCell, setCurrentCell] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [message, setMessage] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [displayTime, setDisplayTime] = useState('0:00');
  const [countdown, setCountdown] = useState('');
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [storedResult, setStoredResult] = useState(null);
  const gridRef = React.useRef(null);
  const containerRef = React.useRef(null);

  const dayNumber = getDayNumber();
  const todaysDifficulty = getTodaysDifficulty(dayNumber);
  const formattedDate = getFormattedDate();

  // Timer
  useEffect(() => {
    if (gameOver || !startTime) return;
    const interval = setInterval(() => {
      const seconds = Math.floor((Date.now() - startTime) / 1000);
      setDisplayTime(`${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`);
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime, gameOver]);

  // Countdown
  useEffect(() => {
    const interval = setInterval(() => setCountdown(getTimeUntilNextPuzzle()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Send height to parent for iframe resizing
  useEffect(() => {
    const sendHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.scrollHeight;
        window.parent.postMessage({
          type: 'RESIZE_IFRAME',
          game: 'shikaku',
          height: height
        }, '*');
      }
    };
    
    sendHeight();
    const resizeObserver = new ResizeObserver(sendHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => resizeObserver.disconnect();
  }, [gameOver, won, rectangles]);

  // Initialize game
  const generateNewPuzzle = useCallback(() => {
    const diff = DIFFICULTIES[todaysDifficulty];
    const seed = dayNumber * 1000 + todaysDifficulty * 100;
    const puzzle = generatePuzzle(diff.size, seed);
    setPuzzleData({ ...puzzle, difficulty: diff.name });
    setRectangles([]);
    setIsDrawing(false);
    setStartCell(null);
    setCurrentCell(null);
    
    const stored = getStoredProgress();
    if (stored?.completed) {
      setAlreadyCompleted(true);
      setStoredResult(stored);
      setGameOver(true);
      setWon(true);
      setDisplayTime(stored.time);
      setMessage('Already completed today!');
    } else {
      setAlreadyCompleted(false);
      setStoredResult(null);
      setGameOver(false);
      setWon(false);
      setStartTime(Date.now());
      setDisplayTime('0:00');
      setMessage('Divide into rectangles!');
    }
  }, [dayNumber, todaysDifficulty]);

  useEffect(() => { generateNewPuzzle(); }, [generateNewPuzzle]);

  // Check win condition
  useEffect(() => {
    if (!puzzleData || rectangles.length === 0 || gameOver) return;
    
    const covered = new Set();
    for (const rect of rectangles) {
      for (let r = rect.minRow; r <= rect.maxRow; r++) {
        for (let c = rect.minCol; c <= rect.maxCol; c++) {
          covered.add(`${r},${c}`);
        }
      }
    }
    
    if (covered.size === puzzleData.size * puzzleData.size) {
      const seconds = Math.floor((Date.now() - startTime) / 1000);
      const timeString = `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
      setDisplayTime(timeString);
      setWon(true);
      setGameOver(true);
      setMessage('Puzzle solved!');
      saveProgress({ completed: true, time: timeString, rectangleCount: rectangles.length });
      
      // Send result to parent window (Webflow)
      window.parent.postMessage({
        type: 'GAME_COMPLETE',
        game: 'shikaku',
        data: {
          completed: true,
          time_seconds: seconds,
          rectangles_used: rectangles.length,
          difficulty: puzzleData.difficulty
        }
      }, '*');
    }
  }, [rectangles, puzzleData, startTime, gameOver]);

  const cellKey = (r, c) => `${r},${c}`;

  const getPreviewRect = () => {
    if (!startCell || !currentCell) return null;
    return {
      minRow: Math.min(startCell.row, currentCell.row),
      maxRow: Math.max(startCell.row, currentCell.row),
      minCol: Math.min(startCell.col, currentCell.col),
      maxCol: Math.max(startCell.col, currentCell.col)
    };
  };

  const getRectArea = (rect) => (rect.maxRow - rect.minRow + 1) * (rect.maxCol - rect.minCol + 1);

  const getNumbersInRect = (rect) => {
    const numbers = [];
    for (let r = rect.minRow; r <= rect.maxRow; r++) {
      for (let c = rect.minCol; c <= rect.maxCol; c++) {
        if (puzzleData.grid[r][c] > 0) numbers.push(puzzleData.grid[r][c]);
      }
    }
    return numbers;
  };

  const isValidRectangle = (rect) => {
    const numbers = getNumbersInRect(rect);
    if (numbers.length !== 1) return false;
    return numbers[0] === getRectArea(rect);
  };

  const doesOverlap = (newRect, existingRects) => {
    for (const rect of existingRects) {
      if (!(newRect.maxRow < rect.minRow || newRect.minRow > rect.maxRow ||
            newRect.maxCol < rect.minCol || newRect.minCol > rect.maxCol)) {
        return true;
      }
    }
    return false;
  };

  const getCellFromPoint = (clientX, clientY) => {
    if (!gridRef.current || !puzzleData) return null;
    const rect = gridRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const cellSize = 44;
    const col = Math.floor(x / cellSize);
    const row = Math.floor(y / cellSize);
    if (row >= 0 && row < puzzleData.size && col >= 0 && col < puzzleData.size) {
      return { row, col };
    }
    return null;
  };

  const handlePointerDown = (e) => {
    if (gameOver) return;
    e.preventDefault();
    e.target.setPointerCapture(e.pointerId);
    
    const cell = getCellFromPoint(e.clientX, e.clientY);
    if (!cell) return;
    
    // Check if clicking existing rectangle to remove it
    const clickedRectIndex = rectangles.findIndex(rect =>
      cell.row >= rect.minRow && cell.row <= rect.maxRow &&
      cell.col >= rect.minCol && cell.col <= rect.maxCol
    );
    
    if (clickedRectIndex !== -1) {
      setRectangles(rectangles.filter((_, i) => i !== clickedRectIndex));
      return;
    }
    
    setIsDrawing(true);
    setStartCell(cell);
    setCurrentCell(cell);
  };

  const handlePointerMove = (e) => {
    if (!isDrawing) return;
    const cell = getCellFromPoint(e.clientX, e.clientY);
    if (cell) setCurrentCell(cell);
  };

  const handlePointerUp = (e) => {
    if (e.pointerId !== undefined) {
      e.target.releasePointerCapture(e.pointerId);
    }
    
    if (!isDrawing || !startCell || !currentCell) {
      setIsDrawing(false);
      return;
    }

    const newRect = getPreviewRect();
    
    if (!doesOverlap(newRect, rectangles) && isValidRectangle(newRect)) {
      setRectangles([...rectangles, { ...newRect, id: Date.now() }]);
      setMessage('Divide into rectangles!');
    } else if (doesOverlap(newRect, rectangles)) {
      setMessage('Rectangles cannot overlap!');
    } else {
      const numbers = getNumbersInRect(newRect);
      const area = getRectArea(newRect);
      if (numbers.length === 0) {
        setMessage('Must contain a number!');
      } else if (numbers.length > 1) {
        setMessage('Only one number allowed!');
      } else {
        setMessage(`Area is ${area}, need ${numbers[0]}!`);
      }
    }

    setIsDrawing(false);
    setStartCell(null);
    setCurrentCell(null);
  };

  const getCellStyle = (row, col) => {
    for (let i = 0; i < rectangles.length; i++) {
      const rect = rectangles[i];
      if (row >= rect.minRow && row <= rect.maxRow && col >= rect.minCol && col <= rect.maxCol) {
        return { backgroundColor: RECT_COLORS[i % RECT_COLORS.length] };
      }
    }
    return { backgroundColor: '#faf6e7' };
  };

  const isInPreview = (row, col) => {
    if (!isDrawing) return false;
    const preview = getPreviewRect();
    if (!preview) return false;
    return row >= preview.minRow && row <= preview.maxRow && col >= preview.minCol && col <= preview.maxCol;
  };

  const getPreviewStyle = () => {
    if (!isDrawing || !puzzleData) return null;
    const preview = getPreviewRect();
    if (!preview) return null;
    if (doesOverlap(preview, rectangles)) return { backgroundColor: '#cccccc' };
    if (isValidRectangle(preview)) return { backgroundColor: '#ffcccc' };
    return { backgroundColor: '#cccccc' };
  };

  const generateShareText = () => {
    if (!puzzleData) return '';
    let text = `📐 Shikaku · ${formattedDate}\n`;
    text += `${puzzleData.size}×${puzzleData.size} ${puzzleData.difficulty}\n`;
    text += `⏱️ ${displayTime} | 📦 ${storedResult?.rectangleCount || rectangles.length} rectangles\n`;
    text += won ? '✅ Solved!' : '❌ Incomplete';
    return text;
  };

  const handleCopy = () => {
    copyToClipboard(
      generateShareText(),
      () => {
        setMessage('Copied to clipboard!');
        setTimeout(() => setMessage(alreadyCompleted ? 'Already completed today!' : 'Puzzle solved!'), 2000);
      },
      () => setMessage('Could not copy')
    );
  };

  const resetPuzzle = () => {
    if (alreadyCompleted) return;
    setRectangles([]);
    setGameOver(false);
    setWon(false);
    setStartTime(Date.now());
    setDisplayTime('0:00');
    setMessage('Divide into rectangles!');
  };

  if (!puzzleData) {
    return <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#faf6e7', color: '#000' }}>Loading...</div>;
  }

  const previewStyle = getPreviewStyle();

  // Styles
  const styles = {
    container: {
      padding: 16,
      maxWidth: 400,
      margin: '0 auto',
      userSelect: 'none',
      backgroundColor: '#faf6e7',
      minHeight: '100vh',
      fontFamily: 'system-ui, sans-serif',
    },
    header: { textAlign: 'center', marginBottom: 16 },
    title: { fontSize: 28, fontWeight: 'bold', marginBottom: 4, color: '#000' },
    subtitle: { color: '#666', fontSize: 14 },
    message: { fontSize: 14, marginTop: 8, fontWeight: 600, color: '#ff0000' },
    statsRow: { display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 16, fontSize: 14 },
    statBadge: { backgroundColor: 'rgba(0,0,0,0.08)', color: '#000', padding: '6px 12px', borderRadius: 6, fontWeight: 500 },
    dateBadge: { backgroundColor: '#ff0000', color: '#fff', padding: '6px 12px', borderRadius: 6, fontWeight: 600 },
    grid: {
      display: 'grid',
      gap: 0,
      margin: '0 auto 16px',
      border: '2px solid #000',
      borderRadius: 4,
      overflow: 'hidden',
      width: 'fit-content',
      touchAction: 'none'
    },
    cell: {
      width: 44,
      height: 44,
      border: '1px solid rgba(0,0,0,0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      fontSize: 18,
      cursor: 'pointer',
      transition: 'background-color 0.1s',
      color: '#000'
    },
    button: (primary) => ({
      padding: '10px 20px',
      borderRadius: 8,
      fontSize: 14,
      fontWeight: 600,
      border: 'none',
      cursor: 'pointer',
      backgroundColor: primary ? '#ff0000' : 'rgba(0,0,0,0.1)',
      color: primary ? '#fff' : '#000'
    }),
    banner: {
      backgroundColor: '#ff0000',
      borderRadius: 12,
      padding: 20,
      marginBottom: 16,
      textAlign: 'center'
    },
    countdownBox: {
      backgroundColor: 'rgba(0,0,0,0.05)',
      borderRadius: 8,
      padding: 12,
      marginBottom: 16,
      border: '1px solid rgba(0,0,0,0.1)',
      textAlign: 'center'
    },
    sharePreview: {
      padding: 12,
      backgroundColor: 'rgba(0,0,0,0.05)',
      borderRadius: 8,
      fontSize: 12,
      marginBottom: 12,
      whiteSpace: 'pre-wrap',
      textAlign: 'left',
      color: '#000',
      border: '1px solid rgba(0,0,0,0.1)'
    },
    rules: { marginTop: 24, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,0.15)' },
    ruleText: { fontSize: 12, color: '#444', lineHeight: 1.6 },
    legend: { display: 'flex', justifyContent: 'center', gap: 16, marginTop: 12, fontSize: 12, color: '#666' },
    legendItem: { display: 'flex', alignItems: 'center', gap: 4 },
    legendDot: (color) => ({ width: 16, height: 16, backgroundColor: color, borderRadius: 3, border: '1px solid rgba(0,0,0,0.15)' })
  };

  return (
    <div ref={containerRef} style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={styles.title}>Shikaku</h2>
        <p style={styles.subtitle}>Divide into rectangles</p>
        <p style={styles.message}>{message}</p>
      </div>

      {/* Stats */}
      <div style={styles.statsRow}>
        <span style={styles.statBadge}>⏱️ {displayTime}</span>
        <span style={styles.statBadge}>{DIFFICULTIES[todaysDifficulty].name}</span>
        <span style={styles.dateBadge}>{formattedDate}</span>
      </div>

      {/* Game grid */}
      <div
        ref={gridRef}
        style={{ ...styles.grid, gridTemplateColumns: `repeat(${puzzleData.size}, 44px)` }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={() => {
          if (isDrawing) {
            setIsDrawing(false);
            setStartCell(null);
            setCurrentCell(null);
          }
        }}
      >
        {puzzleData.grid.map((row, r) =>
          row.map((cell, c) => {
            const inPreview = isInPreview(r, c);
            const cellStyle = inPreview ? previewStyle : getCellStyle(r, c);
            
            return (
              <div key={cellKey(r, c)} style={{ ...styles.cell, ...cellStyle }}>
                {cell > 0 && cell}
              </div>
            );
          })
        )}
      </div>

      {/* Action buttons */}
      {!gameOver ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button onClick={resetPuzzle} style={styles.button(false)}>Reset</button>
        </div>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <div style={styles.banner}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
            <div style={{ fontSize: 22, fontWeight: 'bold', color: '#fff' }}>
              {alreadyCompleted ? 'Already Completed!' : 'Puzzle Solved!'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, marginTop: 4 }}>
              Completed in {displayTime} with {storedResult?.rectangleCount || rectangles.length} rectangles
            </div>
          </div>
          
          <div style={styles.countdownBox}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Next puzzle in</div>
            <div style={{ fontSize: 18, fontWeight: 'bold', color: '#000' }}>{countdown}</div>
          </div>
          
          <pre style={styles.sharePreview}>{generateShareText()}</pre>
          
          <button onClick={handleCopy} style={{ ...styles.button(false), backgroundColor: '#000', color: '#fff' }}>
            📋 Share Result
          </button>
        </div>
      )}

      {/* Rules */}
      <div style={styles.rules}>
        <p style={{ fontSize: 12, color: '#666', textAlign: 'center', marginBottom: 8 }}>How to play:</p>
        <div style={styles.ruleText}>
          <p>• Click and drag to draw rectangles</p>
          <p>• Each rectangle must contain exactly ONE number</p>
          <p>• The number must equal the rectangle's area</p>
          <p>• Cover all cells with no overlaps</p>
          <p>• Click a rectangle to remove it</p>
        </div>
        <div style={styles.legend}>
          <span style={styles.legendItem}><span style={styles.legendDot('#ffcccc')} /> Valid</span>
          <span style={styles.legendItem}><span style={styles.legendDot('#cccccc')} /> Invalid</span>
          <span style={styles.legendItem}><span style={styles.legendDot('#B8D4C8')} /> Placed</span>
        </div>
      </div>
    </div>
  );
};

export default Shikaku;
